import Backbone from 'backbone'
import _ from 'lodash'
import User from './users/user'
import Group from './groups/group'
import Finder from 'scoreboard/components/finder'
import Sifter from 'scoreboard/components/scoreboard-sifter'
import i18n from 'scoreboard/components/i18n'
import icon from 'scoreboard/components/icon'
import constants from 'scoreboard/components/constants'
import permissions from 'scoreboard/components/permissions'
import conductor from 'scoreboard/components/conductor'
import template from './principal-list.hbs'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import guard from 'scoreboard/components/unsaved/guard'
import highlight from 'scoreboard/components/highlight'
import formatValue from 'scoreboard/components/format-value'
import { CHANGE_ITEM_EVENT } from '../admin-two-column-editor'

// #24818 S7 show 100 results for Sandia
var MAX_RESULTS = 100
var SHOW_ALL_RESULTS_UNDER = 100

var PrincipalList = Backbone.View.extend({

	// this.principals is the list of principals (which aren't available
	// initially when render is called)

	className: 'panel left-panel',

	events: {
		'click .list li': 'changePrincipal',
		'click .icon-add': 'add',
		'click .icon-import': 'import'
	},

	/**
	 * @param options.principalId The ID of the selected principal (if there is one)
	 */
	initialize: function(options) {
		this.options = options
		this.finder = new Finder()
		this.sortDirection = 'asc'
		this.hasUserInteracted = false
		this.showImport = options.showImport
			&& permissions.modifyUsers()

		this.listenTo(Backbone, 'add:domain-object', this._addPrincipal)
		this.listenTo(Backbone, 'add:domain-objects', this._updatePrincipals)
		this.listenTo(Backbone, 'edit:domain-object', this._editPrincipal)
		this.listenTo(Backbone, 'delete:domain-object', this._deletePrincipal)

		// Fulfills the ItemList Api contract for admin-two-column-editor.js
		// FIXME: Remove this when full refactoring of principal-list is done
		this.setItems = this.setPrincipals.bind(this)
	},

	setXhr: function(xhr) {
		this.xhr = xhr
	},

	render: function() {
		this.$el.empty().append(template({
			headingKey: this.headingKey,
			showFilter: this.showFilter,
			showImport: this.showImport
		}))

		this.createLoadingDots()

		this.$('.admin-list-view-search-finder').append(this.finder.el)
		this.finder.render()
		this.listenTo(this.finder, 'find', this.find.bind(this))
		// autofocusing is to be handled in autofocus.js
		this.finder.$('input').addClass('autofocus')

		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		// after emptying view need to re-delegate events
		this.delegateEvents(this.events)
		return this
	},

	createLoadingDots: function() {
		this.loadingTimeout = setTimeout(function() {
			this.$('.admin-results-message')
				.addClass('is-loading')
				.removeClass('is-not-displayed')
				.html(loadingDots())
		}.bind(this), constants.LOADING_TIME)
	},

	_interested: function(model) {
		return model instanceof User || model instanceof Group
	},

	// callback after a principal has been added
	_addPrincipal: function(principal) {
		this._updatePrincipals([principal])
		this.setSelectedState(principal.id)
	},

	// updates list using an array of principals; adds any that weren't
	// there, updates any that were. Won't remove any principals
	_updatePrincipals: function(principals) {
		var existingIndexById = {}
		this.principals.forEach(function (p, index) {
			existingIndexById[p.id] = index
		})

		principals.forEach(function (p) {
			if (!this._interested(p)) {
				return
			}

			var existingIndex = existingIndexById[p.id]
			// #9154 #27676 calculate totalMembers so the correct number will
			// be reflected in the principal list after adding a group with members
			// or modifying the members in an existing group.
			// The count is of unique principals belonging to the group.
			let principal = p.toJSON()
			let uniqueMembers = _.unionWith(principal.admins, principal.members, _.isEqual)
			principal.totalMembers = _.size(uniqueMembers)

			if (_.isNil(existingIndex)) {
				this.principals.push(principal)
			} else {
				this.principals[existingIndex] = principal
			}

		}.bind(this))

		this.setPrincipals(this.principals)
	},

	// callback after a principal has been edited
	_editPrincipal: function(principal) {
		this._updatePrincipals([principal])
	},

	// callback after a principal has been deleted
	_deletePrincipal: function(principal) {
		if (!this._interested(principal)) {
			return
		}

		_.remove(this.principals, function(p) {
			return p.id == principal.id
		})
		this.setPrincipals(this.principals)
	},

	setPrincipals: function(principals) {
		this.principals = principals
		this.filteredPrincipals = this.preFilter(principals)
		this.sifter = new Sifter(this.filteredPrincipals)
		delete this.results
		clearTimeout(this.loadingTimeout)
		this.$('.admin-results-message').removeClass('is-loading')
		this.find()
		// #20737 make sure selected principal shows as selected after refresh
		this.setSelectedState(conductor.id())
	},

	/**
	 * Triggered after keyup events from this.finder
	 */
	find: function() {
		//Sifter is only created after server response is handled
		if (!this.sifter) {
			return
		}

		var searchStr = this.finder.text() || ''
		var textEntered = _.isString(this.finder.text())
		this.hasUserInteracted = this.hasUserInteracted || textEntered
		var limit = this._calculateLimit(searchStr)
		this.results = this.sifter.search(searchStr, {
			fields: this.getSifterFields(),
			sort: [this.getSortOptions()],
			conjunction: 'and',
			limit
		})

		this.renderResults()
	},

	/**
	 * Toggles the visibility of the list and search message as appropriate and
	 * show the list of results
	 */
	renderResults: function() {
		var numResults = _.size(_.result(this.results, 'items'))
		var showSearchMsg = numResults === 0 &&
			(_.isEmpty(this.finder.text()) || !this.hasUserInteracted)
		this.$('.admin-results-message').toggleClass('is-not-displayed', !showSearchMsg)
		this.$('.admin-results-message').html(this._getSearchMessage())
		this._showList()
	},

	/**
	 * @return {String[]} The fields to use in the sifter
	 */
	getSifterFields: function() {
		throw Error('Subclass must implement this function')
	},

	/**
	 * Sifter sort options, allows subclasses to provide their own sort field
	 * and direction.
	 * @see group-list-filter.js
	 *
	 * @return {{field: string, direction: string}}
	 */
	getSortOptions: function() {
		return {field: 'label', direction: this.sortDirection}
	},

	/**
	 * Can be overridden by subclasses to filter the principal list before
	 * the Sifter is applied.
	 * @see group-list-filter.js
	 *
	 * @param principals
	 * @return new list of principals
	 */
	preFilter: function(principals) { return principals },

	/**
	 * If there are less than SHOW_ALL_RESULTS_UNDER principals, show all of them.
	 * Else, if there is no search string, show no principals. If there is a search string,
	 * show all of them (results will be clamped in _showList with the maxResults message).
	 * #7296
	 * @param {String} [searchStr ='']
	 */
	_calculateLimit: function(searchStr = '') {
		let total = this._getNumPrincipals()
		if (total < SHOW_ALL_RESULTS_UNDER) {
			return total
		}

		return searchStr == '' ? 0 : total
	},

	_getSearchMessage: function() {
		let size = this._getNumPrincipals()
		let key = 'common.thereAreNo'
		let arg = i18n.get(this.headingKey).toLowerCase()
		// #25884 display number of principals available in search or filter message
		if (size > 0) {
			key = this.searchMessageKey
			arg = highlight(formatValue({value:size, shorten: false}))
		}
		return i18n.get(key, arg)
	},

	_getNumPrincipals: function() {
		return _.size(this.filteredPrincipals)
	},

	_showList: function() {
		var html = ''
		_.each(this.results.items, function(item, index) {
			// don't show all results b/c it could crash the browser if there
			// are a ton of groups (#23176)
			if (index == MAX_RESULTS - 1) {
				let numResults = formatValue({ value: this.results.total })
				html += `<li class="member-item max-results">
					${i18n.get('admin.user.maxResults',
						highlight(numResults), highlight(this.finder.text()))}
				</li>`
				return false
			}
			var principal = this.filteredPrincipals[item.id]
			html += this._renderListItem(item, principal)
		}.bind(this))
		this.$('.list').html(html)
		this.trigger('showList')
	},

	_renderListItem: function(item, principal) {
		let html = `<li class="member-item" data-index="${item.id}" data-id="${principal.id}">` +
			`<div class="content-wrapper">` +
			`<div class="avatar">${icon('user-avatar')}</div>` +
			`<h3>${_.escape(principal.label)}</h3>` +
			`</div></li>`
		return html
	},

	changePrincipal: function(e) {
		var item = this.$(e.target).closest('li')
		,	index = item.data('index')
		,	principal = this.filteredPrincipals[index]

		guard.conditionallyRun(() => {
			this.setSelectedState(principal.id)
			this.trigger(CHANGE_ITEM_EVENT, principal)
		})
	},

	setSelectedState: function (principalId) {
		this.$('.list li').removeClass('is-selected')
		this.$(`.list li[data-id=${principalId}]`).addClass('is-selected')
	},

	add: function() {
		this.trigger('add')
	},

	import: function() {
		this.trigger('import')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.tree, this.finder, this.covertButtonTooltips], 'remove')
	}

})

export default PrincipalList

PrincipalList.__filename__ = 'admin/principals/principal-list.js'
